import {keepPreviousData, useIsFetching, useMutation, useQuery, UseQueryOptions} from "@tanstack/vue-query";
import {
    AccountApi,
    type AccountBankIdModel,
    type AccountModel,
    type PermissionModel
} from "~/gen/openapi/portalService";
import {CUSTOMER_ROLES, CUSTOMER_TYPES, ROLE_FOREST_OWNER} from "~/constants/customer";
import {useUserStore} from "~/stores/user";
import {queryClient} from "~/plugins/vue-query";
import {useAccountId} from "~/composables/useAccountId";

export const useAccountService = () => {
    const { $axios, $config } = useNuxtApp()
    const accountApi = new AccountApi(undefined, $config.public.apiBaseHost, $axios)
    const userStore = useUserStore()
    const accountId = useAccountId()

    const getAccountsQueryOptions = {
        queryKey: ['accountGetAccountsByCurrentUser'],
        queryFn: async () => {
            return (await accountApi.accountGetAccountsByCurrentUser()).data as Array<PermissionModel>
        },
        staleTime: 60 * 60 * 1000,
        enabled: computed(() => userStore.isLoggedIn),
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData
    }

    const accountsQuery = () => useQuery(getAccountsQueryOptions)
    const { data: accountsData } = accountsQuery()
    const accounts = computed(() => accountsData.value)

    const getAccountQueryOptions = (id: string): UseQueryOptions<AccountModel> => {
        return {
            queryKey: ['accountGetAccount', id],
            queryFn: async () => {
                return (await accountApi.accountGetAccount(id!))?.data
            },
            enabled: computed(() => !!id && !!id?.length),
            staleTime: 15 * 60 * 1000,
            refetchOnWindowFocus: false,
            placeholderData: keepPreviousData
        }
    }

    const activeAccountData = (id?: string) => {
        return queryClient.getQueryData(getAccountQueryOptions(id || accountId.value).queryKey)
    }

    const accountQuery = (id: string) => {
        const options = getAccountQueryOptions(id)
        return useQuery(options)
    }

    const getAccountName = () => {
        const account = accounts.value?.find((account) => account.AccountId === accountId.value)
        return account?.EntrepreneurName || account?.AccountName || ''
    }

    const getAccountPropertiesContactsQueryOptions = (id?: string) => {
        return {
            queryKey: ['accountGetAccountPropertiesContacts', computed(() => id)],
            queryFn: async () => {
                return (await accountApi.accountGetAccountPropertiesContacts(id!)).data
            },
            enabled: computed(() => !!id),
            staleTime: 60 * 60 * 1000,
            refetchOnWindowFocus: false
        }
    }

    const contactsQuery = (id?: string) => useQuery(getAccountPropertiesContactsQueryOptions(id || accountId.value))

    const primaryForestOwnerAccountPermissionModel = () => {
        return accounts.value?.find((account) => account.Role === ROLE_FOREST_OWNER && account.IsPrimaryAccount)
    }

    const primaryForestOwnerAccountQuery = () => {
        const primaryForestOwner = primaryForestOwnerAccountPermissionModel()
        const id = primaryForestOwner?.AccountId || ''
        return useQuery(getAccountQueryOptions(id))
    }

    const getAccountContactsQueryOptions = (id?: string) => {
        return {
            queryKey: ['accountGetAccountContacts', id],
            queryFn: async () => {
                return (await accountApi.accountGetAccountContacts(id!)).data
            },
            enabled: computed(() => !!id),
            staleTime: 60 * 60 * 1000,
            refetchOnWindowFocus: false
        }
    }

    const accountContactsQuery = (id?: string) => useQuery(getAccountContactsQueryOptions(id || accountId.value))

    const { isPending: isPendingSaveAccount, error: saveAccountError, isSuccess: saveAccountSuccess, mutate: saveAccount } = useMutation({
        mutationFn: (data: AccountBankIdModel) => {
            if (!data.Account?.Id) {
                throw new Error('Missing account id')
            }
            return accountApi.accountUpdateAccountBankId(data.Account.Id, data)
        },
        onSuccess(response) {
            queryClient.setQueryData(['accountGetAccount', accountId.value],
                (oldData: AccountModel) => {
                    const responseData = response.data as unknown as AccountModel
                    if (responseData.Id) {
                        return responseData
                    }

                    return oldData
                })
        },
        onError(error, _variables) {
            console.error(error)
        },
    })

    const accountRoles = computed(() => {
        const a: any[] = []
        // Role 1: Skogeier, Role 2: Entreprenør. Role 4: Accountant,
        // AccessLevel 1: Full (admin), AccessLevel 2: Limited.
        CUSTOMER_ROLES.forEach((role) => {
            const roleAccounts = accounts.value?.filter(
                (account) => account.Role === role.id
            )

            if (roleAccounts?.length) {
                a.push({
                    accounts: roleAccounts,
                    name: role.name,
                    path: role.path,
                    id: role.id,
                })
            }
        })

        return a
    })

    const accountRole = (accountId: string) => {
        const account = accounts.value?.find((account) => account.AccountId === accountId)
        return account ? account.Role : null
    }

    // https://dev.azure.com/GlommenMjosen/Portaler%20og%20SBL-app/_workitems/edit/1538
    // Properties (and by extension - accounts) can be marked as "excluded" from the normal verification flow.
    const getUserShouldVerify = (primaryForestOwnerAccount: Ref<AccountModel>) => {
        const {$config} = useNuxtApp()
        if ($config.public.features.excludeAllFromVerification) {
            return false
        }
        return primaryForestOwnerAccount.value?.IsExcludedFromVerification === false
    }

    /**
     * Uses active account if no accountId is provided
     */
    const isSilvicultureAccount = (accId?: string) => {
        const id = accId || accountId.value
        if (!id) {
            throw new Error('Failed to check silviculture account: missing account id')
        }

        const account = accounts.value?.find((account) => account.AccountId === id)
        return account && account.Role === 2
    }

    const getBasePath = () => {
        if (!accountId.value) {
            return '/'
        }

        const account = accounts.value?.find((item) => item.AccountId === accountId.value)
        if (account && account.Role) {
            const role = CUSTOMER_ROLES.find((role) => role.id === account.Role)
            return role ? `/${role.path}/${accountId.value}` : '/'
        }
        return '/'
    }

    const accountantId = computed(() => {
        return activeAccountData()?.AccountantId
    })
1
    const accountantName = computed(() => {
        return activeAccountData()?.AccountantName
    })

    const hasRole = (roleType: keyof typeof CUSTOMER_TYPES) => {
        return (
            roleType in CUSTOMER_TYPES &&
            activeAccountData() &&
            'CustomerType' in activeAccountData() &&
            CUSTOMER_TYPES[roleType].includes(activeAccountData().CustomerType!)
        )
    }

    const accountTeamName = computed(() => {
        const accountData = activeAccountData()
        return accountData
            // @ts-ignore
            ? accountData.EntrepreneurName &&   // @ts-ignore
            accountData.AccountName &&  // @ts-ignore
            accountData.AccountName.indexOf(accountData.EntrepreneurName) === 0	// @ts-ignore
                ? accountData.AccountName.substring(    // @ts-ignore
                    accountData.EntrepreneurName.length + 1
                )   // @ts-ignore
                : accountData.AccountName
            : ''
    })

    /**
     * If access level is 1, or if user has another account with the same contractor ID and access level 1
     */
    const getHasAdminAccess = () => {
        const accountData = accounts.value?.find((account) => account.AccountId === accountId.value)
        return accountData && accountData.AccessLevel === 1
    }

    const fetchingAccountsQuery = useIsFetching({ queryKey: getAccountsQueryOptions.queryKey })
    const isFetchingAccounts = computed(() => fetchingAccountsQuery.value > 0)

    const fetchingAccountQuery = useIsFetching({ queryKey: getAccountQueryOptions(accountId.value).queryKey })
    const isFetchingActiveAccount = computed(() => fetchingAccountQuery.value > 0)

    return {
        accountId,
        getAccountsQueryOptions,
        accountsQuery,
        getAccountQueryOptions,
        accountQuery,
        hasRole,
        primaryForestOwnerAccountQuery,
        primaryForestOwnerAccountPermissionModel,
        accountRoles,
        accountRole,
        getUserShouldVerify,
        isSilvicultureAccount,
        getBasePath,
        getAccountName,
        accountantId,
        accountantName,
        accountTeamName,
        contactsQuery,
        accountContactsQuery,
        isPendingSaveAccount,
        saveAccountError,
        saveAccountSuccess,
        saveAccount,
        getHasAdminAccess,
        isFetchingAccounts,
        isFetchingActiveAccount,
    }
}